/**
 * TAG SUGGESTION SERVICE - Cliente para sugerencias de etiquetas con IA
 * 
 * Llama a la Cloud Function que usa Structured Outputs para garantizar
 * que las sugerencias priorizan las etiquetas existentes del terapeuta.
 */

import { functions } from '@/core/config/firebase';
import { Logger } from '@/core/utils';

const logger = Logger.getLogger('TagSuggestionService');

// ============================================
// TYPES
// ============================================

export interface TagSuggestion {
    tag: string;
    isExisting: boolean;  // true = del catálogo existente, false = nueva sugerida
    relevanceScore: number;  // 0.0 - 1.0
    reason?: string;
}

export interface SuggestTagsResult {
    suggestions: TagSuggestion[];
    analysisConfidence: number;
}

interface SuggestTagsRequest {
    content: string;
    availableTags: string[];
    maxSuggestions?: number;
    therapistId?: string;
}

// ============================================
// SERVICE
// ============================================

const suggestTagsForContent = functions.httpsCallable('suggestTagsForContent', {
    timeout: 60000  // 1 minute
});

/**
 * Sugiere etiquetas para un contenido usando IA con Structured Outputs
 * 
 * @param content - Texto a analizar (OCR, transcripción, nota)
 * @param availableTags - Etiquetas existentes del terapeuta
 * @param options - Opciones adicionales
 * @returns Sugerencias de etiquetas priorizando las existentes
 */
export async function suggestTags(
    content: string,
    availableTags: string[],
    options?: {
        maxSuggestions?: number;
        therapistId?: string;
    }
): Promise<SuggestTagsResult> {

    // Validar contenido mínimo
    if (!content || content.length < 20) {
        logger.warn('Content too short for tag suggestion');
        return { suggestions: [], analysisConfidence: 0 };
    }

    try {
        logger.info('Requesting tag suggestions', {
            contentLength: content.length,
            availableTagsCount: availableTags.length
        });

        const request: SuggestTagsRequest = {
            content,
            availableTags,
            maxSuggestions: options?.maxSuggestions || 5,
            therapistId: options?.therapistId
        };

        const result = await suggestTagsForContent(request);
        const data = result.data as SuggestTagsResult;

        logger.info('Tag suggestions received', {
            suggestionsCount: data.suggestions?.length || 0,
            existingCount: data.suggestions?.filter(s => s.isExisting).length || 0,
            newCount: data.suggestions?.filter(s => !s.isExisting).length || 0,
            confidence: data.analysisConfidence
        });

        return data;

    } catch (error: any) {
        logger.error('Failed to get tag suggestions', error);

        // Fallback: devolver array vacío para no bloquear el flujo
        return { suggestions: [], analysisConfidence: 0 };
    }
}

/**
 * Versión rápida que solo devuelve los nombres de las etiquetas
 */
export async function suggestTagNames(
    content: string,
    availableTags: string[],
    maxSuggestions = 5
): Promise<string[]> {
    const result = await suggestTags(content, availableTags, { maxSuggestions });
    return result.suggestions.map(s => s.tag);
}

/**
 * Obtiene solo las etiquetas existentes que sean relevantes
 */
export async function suggestExistingTags(
    content: string,
    availableTags: string[],
    maxSuggestions = 5
): Promise<string[]> {
    const result = await suggestTags(content, availableTags, { maxSuggestions: maxSuggestions * 2 });
    return result.suggestions
        .filter(s => s.isExisting)
        .slice(0, maxSuggestions)
        .map(s => s.tag);
}
